/**
 * Shared environment gates for features that are not ready for every audience.
 *
 * There are exactly two shapes, and they were previously hand-copied as raw
 * `process.env.REACT_APP_ENV !== 'prod' && !== 'slava'` expressions at each
 * call site. That drifted: some features meant to be reviewable on slava were
 * silently dev-only, and the Digestion section had no gate at all and shipped
 * to prod. Naming the two shapes here makes each call site state its intent,
 * and makes the inventory in docs/feature-visibility.md checkable.
 *
 * ── The three environments ──────────────────────────────────────────────────
 *   local dev  `npm start` / jest   NODE_ENV=development, REACT_APP_ENV unset
 *   slava      the staging deploy   REACT_APP_ENV='slava'
 *   prod       site.labsmarts.com   REACT_APP_ENV='prod'
 *
 * ── Failing closed ─────────────────────────────────────────────────────────
 * Only scripts/deploy.js sets REACT_APP_ENV, so a plain `npm run build` leaves
 * it unset. That must read as "hidden", NOT as "not prod" — which is exactly
 * what a bare `!== 'prod'` would wrongly do, shipping an unreleased feature to
 * anyone who built without the deploy script. Hence the `!!REACT_APP_ENV`
 * guard on the production branch.
 *
 * Functions rather than consts so they re-read the env per call, which is what
 * lets tests exercise the prod path; CRA still inlines the reads at build time.
 */

/**
 * Visible in local dev AND on slava, hidden in prod.
 *
 * The normal state for a feature that is finished enough for the user to review
 * on slava but not yet released to practitioners.
 */
export function visibleExceptProd(): boolean {
  if (process.env.REACT_APP_ENV === 'prod') return false;
  if (process.env.NODE_ENV !== 'production') return true;
  return !!process.env.REACT_APP_ENV;
}

/**
 * Visible in local dev ONLY, hidden on slava and prod.
 *
 * For work whose CONTENT is not ready to be seen at all — typically pending a
 * clinical review — as opposed to work that is merely unreleased. Prefer
 * visibleExceptProd() unless there is a reason the user should not see it on
 * slava; being unable to review it there is the cost of this one.
 */
export function devOnly(): boolean {
  return process.env.NODE_ENV !== 'production' && !process.env.REACT_APP_ENV;
}
